import {
  ComplexFieldQueryModel,
  ICustomFieldQueryModel
} from 'src/app/shared/components/odata-filter/models/custom-field-query.model';
import { Image } from 'src/app/people/models/image';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { IFeaturedPersonCustomField } from './featuredPearsonCutomField.model';
import { IPersonRace } from './personRace.model';
import { IPersonTag } from './personTag.model';
import { IPersonWorkspace } from './person-workspace.model';
import { DateRange } from 'src/app/shared/models/dateRange';
import { Position } from 'src/app/shared/models/team/position';
import { Seat } from 'src/app/shared/models/team/seat';
import _ from 'lodash';
import { IEvaluationAggregateQueryModel } from 'src/app/shared/models/evaluation/evaluationAggregateQueryModel';
import moment from 'moment';
import { IPersonNotes } from './personNotes.model';
import { PersonCertificationModel } from './perosn-certification.model';

export interface IPeople {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  imageId: string;
  rating: number;
  status: PersonStatus;
  city: string;
  company: string;
  province: string;
  state: string;
  streetAddress1: string;
  streetAddress2: string;
  country: string;
  email: string;
  gender: string;
  jobTitle: string;
  linkedInProfile: string;
  phoneNumber: string;
  externalSystemId: string;
  postalCode: string;
  updatedOn: Date;
  customFields: ICustomFieldQueryModel[];
  complexCustomFields: ComplexFieldQueryModel[];
  tags: IPersonTag[];
  notes: IPersonNotes[];
  races: IPersonRace[];
  applications: { title: string; isUsed: boolean }[];
  certifications: PersonCertificationModel[];
  selected: boolean;
  workspaces: IPersonWorkspace[];
  evaluationAggregates: IEvaluationAggregateQueryModel[];
  isUnsubscribed: string;
  activeTeamSummary: { activeTeamsCount: number }[];
  allTeamSummary: { allTeamsCount: number }[];
}

export class People implements IPeople {
  public id!: string;
  public fullName!: string;
  public imageId!: string;
  public image!: Image;
  public rating!: number;
  public activeTeamsCount!: number;
  public status!: PersonStatus;
  public province!: string;
  public state!: string;
  public streetAddress1!: string;
  public streetAddress2!: string;
  public phoneNumber!: string;
  public externalSystemId!: string;
  public postalCode!: string;
  public firstName!: string;
  public middleName!: string;
  public lastName!: string;
  public allTeamsCount!: number;
  public city!: string;
  public company!: string;
  public country!: string;
  public email!: string;
  public gender!: string;
  public jobTitle!: string;
  public linkedInProfile!: string;
  public updatedOn!: Date;
  public featuredCustomFields: IFeaturedPersonCustomField[] = [];
  public customFields: ICustomFieldQueryModel[] = [];
  public complexCustomFields: ComplexFieldQueryModel[] = [];
  public tags: IPersonTag[] = [];
  public notes: IPersonNotes[] = [];
  public races: IPersonRace[] = [];
  public applications: { title: string; isUsed: boolean }[] = [];
  public certifications: PersonCertificationModel[] = [];
  public selected: boolean;
  public workspaces: IPersonWorkspace[];

  public evaluationAggregates: IEvaluationAggregateQueryModel[];
  public isUnsubscribed!: string;
  public activeTeamSummary: { activeTeamsCount: number }[] = [];
  public allTeamSummary: { allTeamsCount: number }[] = [];

  get racesFlatValue(): string {
    return this.races ? this.races.map((x) => x.name).join('\n') : '';
  }
  get submittedApplicationsFlatValue(): string {
    //Note: this filter is matched with the CSV export service in the Bus project. Any change here has to reflect there too
    return this.applications
      ? this.applications
          .filter((x) => x.isUsed)
          .map((x) => x.title)
          .join('\n')
      : '';
  }

  get activeCertificationsFlatValue(): string {
    //Note: this filter is matched with the CSV export service in the Bus project. Any change here has to reflect there too
    return this.certifications
      ? this.certifications
          .filter((x) => x.activeRange.todayIsWithinDateRange())
          .map((x) => x.name)
          .join('\n')
      : '';
  }

  get allCertificationsFlatValue(): string {
    //Note: this filter is matched with the CSV export service in the Bus project. Any change here has to reflect there too
    return this.certifications ? this.certifications.map((x) => x.name).join('\n') : '';
  }

  get workspacesFlatValue(): string {
    //Note: this filter is matched with the CSV export service in the Bus project. Any change here has to reflect there too
    return this.workspaces ? this.workspaces.map((x) => x.workspaceName).join('\n') : '';
  }

  getSeatInformation(positions: Position[]): Seat {
    let people = this;
    let selectedSeat: Seat = null;
    _.each(positions, function (position) {
      _.each(position.seats, function (seat) {
        if (seat.person.id === people.id) {
          selectedSeat = seat;
        }
      });
    });
    return selectedSeat;
  }

  hasRating() {
    return this.rating && this.rating > 0;
  }

  isActive() {
    return this.status == PersonStatus.Active;
  }
  isPotential() {
    return this.status == PersonStatus.Potential;
  }
  isArchived() {
    return this.status == PersonStatus.Archived;
  }
  static parse(data: IPeople | Partial<IPeople>) {
    if (!data) return null;
    let people = new People();
    Object.assign(people, data);
    people.isUnsubscribed = data.isUnsubscribed ? 'UnSubscribed' : 'Subscribed';
    if (people.imageId) {
      people.image = new Image(people.imageId);
    }
    people.activeTeamsCount = data.activeTeamSummary?.[0]?.activeTeamsCount || 0;
    people.allTeamsCount = data.allTeamSummary?.[0]?.allTeamsCount || 0;
    if (people.customFields && people.customFields.length) {
      people.featuredCustomFields = people.customFields
        .filter((x) => x.featured)
        .sort((a, b) => a.value.localeCompare(b.value));
    }
    if (people.tags && people.tags.length) {
      people.tags = [...people.tags].sort((a, b) => a.text.localeCompare(b.text));
    }
    people.customFields
      .filter((x) => x.dateRangeAttributeStartDateValue)
      .forEach((x) => {
        let dates = x.value.split('|');
        x.value = `${moment(dates[0]).format('MM/DD/YYYY')} - ${
          dates[1] != 'Present' ? moment(dates[1]).format('MM/DD/YYYY') : dates[1]
        }`;
      });

    people.complexCustomFields = data.complexCustomFields?.map(
      (x) => new ComplexFieldQueryModel(x.id, x.name, x.values)
    );

    people.certifications = data.certifications
      ? data.certifications.map((x) => {
          return {
            activeDate: x.activeDate,
            name: x.name,
            expirationDate: x.expirationDate,
            activeRange: new DateRange(x.activeDate, x.expirationDate)
          };
        })
      : [];
    return people;
  }
}
